import { useState, useEffect, useRef } from "react";

// ─── Design Tokens ───────────────────────────────────────────────
const T = {
  bg: "#080A10",
  surface: "#0F1220",
  surfaceAlt: "#161929",
  card: "#131626",
  border: "#1E2438",
  borderAccent: "#3B82F6",
  text: "#EDF0F7",
  textMuted: "#7A809E",
  textDim: "#454B66",
  accent: "#3B82F6",
  accentLight: "#60A5FA",
  success: "#22C55E",
  successBg: "#22C55E12",
  warning: "#F59E0B",
  warningBg: "#F59E0B12",
  danger: "#EF4444",
  dangerBg: "#EF444412",
  purple: "#A855F7",
  purpleBg: "#A855F712",
  teal: "#14B8A6",
  tealBg: "#14B8A612",
  pink: "#EC4899",
  pinkBg: "#EC489912",
  gradient: "linear-gradient(135deg, #3B82F6, #8B5CF6)",
  gradientWarm: "linear-gradient(135deg, #F59E0B, #EF4444)",
  gradientSuccess: "linear-gradient(135deg, #22C55E, #14B8A6)",
  radius: 14,
  radiusSm: 10,
  radiusLg: 20,
  font: "'Sora', 'DM Sans', -apple-system, sans-serif",
  fontMono: "'JetBrains Mono', 'Fira Code', monospace",
};

// ─── Google Fonts ────────────────────────────────────────────────
const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: ${T.bg}; color: ${T.text}; font-family: ${T.font}; }
    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: ${T.surface}; }
    ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 4px; }
    input, button, textarea { font-family: ${T.font}; }
    @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
    @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .fade-up { animation: fadeUp 0.4s ease both; }
    .fade-up-1 { animation: fadeUp 0.4s 0.05s ease both; }
    .fade-up-2 { animation: fadeUp 0.4s 0.1s ease both; }
    .fade-up-3 { animation: fadeUp 0.4s 0.15s ease both; }
    .fade-up-4 { animation: fadeUp 0.4s 0.2s ease both; }
    .fade-up-5 { animation: fadeUp 0.4s 0.25s ease both; }
    .fade-up-6 { animation: fadeUp 0.4s 0.3s ease both; }
    .btn-hover:hover { filter: brightness(1.12); transform: translateY(-1px); }
    .btn-hover:active { transform: translateY(0); }
    .card-hover:hover { border-color: ${T.borderAccent} !important; transform: translateY(-2px); box-shadow: 0 8px 32px rgba(59,130,246,0.08) !important; }
    .nav-link:hover { color: ${T.text} !important; }
    input:focus { border-color: ${T.accent} !important; outline: none; }
    textarea:focus { border-color: ${T.accent} !important; outline: none; }
    .option-btn:hover { border-color: ${T.accent} !important; background: ${T.accent}14 !important; }
    .glow-dot { width: 6px; height: 6px; border-radius: 50%; background: ${T.success}; box-shadow: 0 0 8px ${T.success}; animation: pulse 2s infinite; }
  `}</style>
);

// ─── Icons ───────────────────────────────────────────────────────
const Icon = ({ name, size = 20, color = T.textMuted }) => {
  const icons = {
    home: <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1" />,
    user: <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />,
    book: <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />,
    check: <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />,
    lock: <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />,
    unlock: <path d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />,
    chat: <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />,
    spark: <path d="M13 10V3L4 14h7v7l9-11h-7z" />,
    chart: <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>,
    mentor: <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />,
    path: <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />,
    grid: <path d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />,
    arrow: <path d="M13 7l5 5m0 0l-5 5m5-5H6" />,
    brain: <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />,
    globe: <path d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
    mail: <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />,
    clock: <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />,
    star: <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />,
    trophy: <path d="M5 3h14M9 3v2a3 3 0 106 0V3m-3 12v4m-4 0h8M5 3a2 2 0 00-2 2v2a4 4 0 004 4h0m12-8a2 2 0 012 2v2a4 4 0 01-4 4h0" />,
    chevron: <path d="M9 5l7 7-7 7" />,
    menu: <path d="M4 6h16M4 12h16M4 18h16" />,
    x: <path d="M6 18L18 6M6 6l12 12" />,
    send: <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      {icons[name]}
    </svg>
  );
};

// ─── Shared Components ───────────────────────────────────────────
const Badge = ({ children, color = T.accent, bg }) => (
  <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, letterSpacing: 0.4, background: bg || `${color}18`, color, whiteSpace: "nowrap" }}>
    {children}
  </span>
);

const ProgressBar = ({ value, max = 100, color = T.accent, height = 6, label }) => (
  <div style={{ width: "100%" }}>
    {label && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 12, color: T.textMuted }}><span>{label}</span><span>{value}/{max === 100 ? "100%" : max}</span></div>}
    <div style={{ width: "100%", height, borderRadius: height, background: T.surfaceAlt }}>
      <div style={{ width: `${(value / max) * 100}%`, height: "100%", borderRadius: height, background: color, transition: "width 0.6s ease" }} />
    </div>
  </div>
);

const Card = ({ children, style, glow, onClick, className }) => (
  <div onClick={onClick} className={className} style={{ background: T.card, border: `1px solid ${glow ? T.borderAccent + "80" : T.border}`, borderRadius: T.radius, padding: 20, transition: "all 0.2s", ...(glow ? { boxShadow: `0 0 30px ${T.accent}10` } : {}), ...(onClick ? { cursor: "pointer" } : {}), ...style }}>
    {children}
  </div>
);

const SectionTitle = ({ icon, title, badge, size = "md" }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: size === "lg" ? 24 : 16 }}>
    {icon && <div style={{ width: 32, height: 32, borderRadius: 8, background: `${T.accent}18`, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name={icon} size={16} color={T.accent} /></div>}
    <span style={{ fontSize: size === "lg" ? 18 : 13, fontWeight: 700, color: T.text, letterSpacing: 0.2, textTransform: size === "lg" ? "none" : "uppercase" }}>{title}</span>
    {badge && <Badge>{badge}</Badge>}
  </div>
);

const ScoreCircle = ({ score, max = 10, size = 64, label, color = T.accent }) => {
  const pct = (score / max) * 100;
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.surfaceAlt} strokeWidth={4} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={4} strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)} strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`} style={{ transition: "stroke-dashoffset 0.8s ease" }} />
        <text x={size/2} y={size/2 + 1} textAnchor="middle" dominantBaseline="middle" fill={T.text} fontSize={size > 50 ? 16 : 13} fontWeight="700" fontFamily={T.font}>{score}</text>
      </svg>
      {label && <span style={{ fontSize: 11, color: T.textMuted, textAlign: "center", lineHeight: 1.3, maxWidth: 80 }}>{label}</span>}
    </div>
  );
};

const ChatBubble = ({ text, isAI }) => (
  <div style={{ display: "flex", gap: 12, justifyContent: isAI ? "flex-start" : "flex-end", marginBottom: 14 }}>
    {isAI && (
      <div style={{ width: 36, height: 36, borderRadius: "50%", background: T.gradient, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: `0 4px 12px ${T.accent}30` }}>
        <span style={{ fontSize: 16 }}>✦</span>
      </div>
    )}
    <div style={{ maxWidth: "72%", padding: "12px 16px", borderRadius: isAI ? "4px 16px 16px 16px" : "16px 4px 16px 16px", background: isAI ? T.surfaceAlt : T.accent, color: T.text, fontSize: 14, lineHeight: 1.6, border: `1px solid ${isAI ? T.border : "transparent"}` }}>
      {text}
    </div>
    {!isAI && (
      <div style={{ width: 36, height: 36, borderRadius: "50%", background: T.purple, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 13, fontWeight: 700, color: "#fff" }}>
        AW
      </div>
    )}
  </div>
);

const Btn = ({ children, variant = "primary", onClick, style: s, small, full }) => {
  const base = { display: "inline-flex", alignItems: "center", gap: 8, border: "none", borderRadius: T.radiusSm, cursor: "pointer", fontWeight: 600, fontFamily: T.font, fontSize: small ? 12 : 14, padding: small ? "7px 14px" : "11px 20px", transition: "all 0.18s", ...(full ? { width: "100%", justifyContent: "center" } : {}) };
  const variants = {
    primary: { background: T.accent, color: "#fff", boxShadow: `0 4px 16px ${T.accent}35` },
    secondary: { background: T.surfaceAlt, color: T.text, border: `1px solid ${T.border}` },
    ghost: { background: "transparent", color: T.accentLight, padding: small ? "6px 10px" : "10px 16px" },
    success: { background: T.success, color: "#fff", boxShadow: `0 4px 16px ${T.success}30` },
    danger: { background: T.danger, color: "#fff" },
  };
  return <button onClick={onClick} className="btn-hover" style={{ ...base, ...variants[variant], ...s }}>{children}</button>;
};

const TabBar = ({ tabs, active, onChange }) => (
  <div style={{ display: "flex", gap: 4, background: T.surfaceAlt, borderRadius: T.radiusSm, padding: 4, marginBottom: 24, border: `1px solid ${T.border}` }}>
    {tabs.map(t => (
      <button key={t} onClick={() => onChange(t)} style={{ flex: 1, padding: "9px 14px", borderRadius: 7, border: "none", background: active === t ? T.card : "transparent", color: active === t ? T.text : T.textMuted, fontSize: 13, fontWeight: active === t ? 600 : 400, cursor: "pointer", fontFamily: T.font, transition: "all 0.2s", boxShadow: active === t ? `0 2px 8px rgba(0,0,0,0.3)` : "none" }}>{t}</button>
    ))}
  </div>
);

const Divider = ({ label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
    <div style={{ flex: 1, height: 1, background: T.border }} />
    {label && <span style={{ fontSize: 10, color: T.textDim, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase" }}>{label}</span>}
    <div style={{ flex: 1, height: 1, background: T.border }} />
  </div>
);

const StatBox = ({ value, label, color = T.accent }) => (
  <div style={{ flex: 1, padding: "14px 10px", background: T.surfaceAlt, borderRadius: T.radiusSm, textAlign: "center", border: `1px solid ${T.border}` }}>
    <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{label}</div>
  </div>
);

// ─── TOP NAV ─────────────────────────────────────────────────────
const TopNav = ({ screen, onNavigate }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navItems = [
    { icon: "home", label: "Home", screen: "welcome" },
    { icon: "book", label: "Train", screen: "layer1-hub" },
    { icon: "chat", label: "Check-in", screen: "layer2-checkin" },
    { icon: "target", label: "Challenge", screen: "challenge" },
    { icon: "chart", label: "Dashboard", screen: "dashboard" },
  ];
  return (
    <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, background: `${T.surface}e8`, backdropFilter: "blur(20px)", borderBottom: `1px solid ${T.border}`, height: 64 }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", height: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px" }}>
        {/* Logo */}
        <button onClick={() => onNavigate("welcome")} style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer" }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: T.gradient, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 12px ${T.accent}40` }}>
            <span style={{ fontSize: 16 }}>✦</span>
          </div>
          <div>
            <span style={{ fontSize: 15, fontWeight: 800, color: T.text, letterSpacing: -0.3 }}>Peer Career</span>
            <span style={{ fontSize: 15, fontWeight: 800, background: T.gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginLeft: 6 }}>Accelerate</span>
          </div>
        </button>

        {/* Desktop Nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          {navItems.map(n => (
            <button key={n.label} onClick={() => onNavigate(n.screen)} className="nav-link" style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 8, border: "none", background: screen === n.screen ? `${T.accent}14` : "transparent", color: screen === n.screen ? T.accentLight : T.textMuted, fontSize: 13, fontWeight: screen === n.screen ? 600 : 400, cursor: "pointer", fontFamily: T.font, transition: "all 0.15s" }}>
              <Icon name={n.icon} size={15} color={screen === n.screen ? T.accent : T.textMuted} />
              {n.label}
            </button>
          ))}
        </div>

        {/* User */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="glow-dot" />
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: T.gradient, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer", boxShadow: `0 4px 12px ${T.purple}30` }}>
            AW
          </div>
        </div>
      </div>
    </nav>
  );
};

// ─── LAYOUT WRAPPER ───────────────────────────────────────────────
const PageLayout = ({ children, maxWidth = 840, style }) => (
  <div style={{ minHeight: "100vh", paddingTop: 80, paddingBottom: 60, background: T.bg }}>
    {/* Background grid */}
    <div style={{ position: "fixed", inset: 0, backgroundImage: `radial-gradient(circle at 20% 20%, ${T.accent}08 0%, transparent 50%), radial-gradient(circle at 80% 80%, ${T.purple}06 0%, transparent 50%)`, pointerEvents: "none" }} />
    <div style={{ maxWidth, margin: "0 auto", padding: "0 24px", position: "relative", ...style }}>
      {children}
    </div>
  </div>
);

// ─── SCREEN 1: Welcome ───────────────────────────────────────────
const WelcomeScreen = ({ onNavigate }) => (
  <PageLayout maxWidth={1200}>
    <div style={{ minHeight: "calc(100vh - 120px)", display: "flex", flexDirection: "column", justifyContent: "center" }}>
      {/* Hero */}
      <div style={{ textAlign: "center", marginBottom: 80 }}>
        <div className="fade-up" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 16px", borderRadius: 20, background: `${T.accent}12`, border: `1px solid ${T.accent}25`, fontSize: 12, fontWeight: 600, color: T.accentLight, marginBottom: 32 }}>
          <div className="glow-dot" />
          AI-Powered Career Development Platform
        </div>
        <h1 className="fade-up-1" style={{ fontSize: 72, fontWeight: 800, color: T.text, lineHeight: 1.1, letterSpacing: -2, marginBottom: 20 }}>
          Accelerate your
          <br />
          <span style={{ background: T.gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>career trajectory</span>
        </h1>
        <p className="fade-up-2" style={{ fontSize: 18, color: T.textMuted, maxWidth: 540, margin: "0 auto 40px", lineHeight: 1.7 }}>
          Build workplace readiness through AI-powered simulations, track your growth across five key dimensions, and unlock your career potential.
        </p>
        <div className="fade-up-3" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Btn onClick={() => onNavigate("onboarding")} style={{ padding: "14px 32px", fontSize: 15 }}>
            Get Started <Icon name="arrow" size={16} color="#fff" />
          </Btn>
          <Btn variant="secondary" onClick={() => onNavigate("layer1-hub")} style={{ padding: "14px 32px", fontSize: 15 }}>
            Welcome back — Resume
          </Btn>
        </div>
      </div>

      {/* Feature Cards */}
      <div className="fade-up-4" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
        {[
          { icon: "chat", title: "Scenario Simulations", desc: "Practice real workplace situations with AI-powered roleplay that adapts to your responses in real time.", color: T.accent, stat: "12+ scenarios" },
          { icon: "chart", title: "Growth Tracking", desc: "Monitor 5 career dimensions across 6 weeks of check-ins. See exactly where you're improving.", color: T.success, stat: "5 dimensions" },
          { icon: "spark", title: "Career Accelerator", desc: "Get data-backed career path recommendations based on your actual performance patterns.", color: T.purple, stat: "3 path options" },
        ].map((f, i) => (
          <Card key={i} className="card-hover" style={{ padding: 28, transition: "all 0.25s" }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: `${f.color}15`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, border: `1px solid ${f.color}20` }}>
              <Icon name={f.icon} size={22} color={f.color} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 8 }}>{f.title}</div>
            <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.65, marginBottom: 16 }}>{f.desc}</p>
            <Badge color={f.color}>{f.stat}</Badge>
          </Card>
        ))}
      </div>

      {/* Stats row */}
      <div className="fade-up-5" style={{ display: "flex", gap: 40, justifyContent: "center", marginTop: 60, padding: "24px 0", borderTop: `1px solid ${T.border}` }}>
        {[{ v: "500+", l: "Early-career professionals" }, { v: "6 weeks", l: "Average program length" }, { v: "87%", l: "Report career progress" }, { v: "4.8★", l: "User satisfaction" }].map(s => (
          <div key={s.l} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: T.text }}>{s.v}</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  </PageLayout>
);

// ─── SCREEN 2: Onboarding ────────────────────────────────────────
const OnboardingScreen = ({ onNavigate }) => {
  const [step, setStep] = useState(0);
  const questions = [
    { q: "What's your name?", placeholder: "e.g. Amina Wanjiku", icon: "user" },
    { q: "What's your current role?", placeholder: "e.g. Junior Software Engineer", icon: "target" },
    { q: "What industry are you in?", options: ["Tech / Fintech", "Consulting", "Healthcare", "NGO / Development", "Government", "Marketing", "Education"], icon: "grid" },
    { q: "What's your work setup?", options: ["Fully remote", "Hybrid", "Fully on-site"], icon: "globe" },
    { q: "What's your biggest growth goal right now?", placeholder: "e.g. Get promoted, improve communication, learn AI tools...", icon: "star" },
    { q: "How comfortable are you with AI tools?", options: ["Never used AI", "Basic — tried ChatGPT", "Regular user", "Power user"], icon: "brain" },
  ];
  const current = questions[step];
  return (
    <PageLayout maxWidth={600}>
      <div className="fade-up" style={{ marginBottom: 40 }}>
        <ProgressBar value={step + 1} max={questions.length} color={T.accent} height={3} />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 12, color: T.textDim }}>
          <span>Step {step + 1} of {questions.length}</span>
          <span>{Math.round(((step + 1) / questions.length) * 100)}% complete</span>
        </div>
      </div>

      <Card className="fade-up-1" style={{ padding: 40 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
          <div style={{ width: 44, height: 44, borderRadius: 11, background: `${T.accent}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name={current.icon} size={22} color={T.accent} />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{current.q}</h2>
        </div>

        {current.options ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {current.options.map(o => (
              <button key={o} className="option-btn" onClick={() => step < questions.length - 1 ? setStep(step + 1) : onNavigate("profile")} style={{ padding: "16px 20px", borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.surfaceAlt, color: T.text, fontSize: 14, textAlign: "left", cursor: "pointer", fontFamily: T.font, transition: "all 0.2s", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {o}
                <Icon name="chevron" size={16} color={T.textDim} />
              </button>
            ))}
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10 }}>
            <input placeholder={current.placeholder} style={{ flex: 1, padding: "14px 18px", borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.surfaceAlt, color: T.text, fontSize: 14, transition: "border-color 0.2s" }} />
            <Btn onClick={() => step < questions.length - 1 ? setStep(step + 1) : onNavigate("profile")} style={{ padding: "14px 20px" }}>
              Continue <Icon name="arrow" size={16} color="#fff" />
            </Btn>
          </div>
        )}
      </Card>
    </PageLayout>
  );
};

// ─── SCREEN 3: Profile ───────────────────────────────────────────
const ProfileScreen = ({ onNavigate }) => (
  <PageLayout maxWidth={680}>
    <div className="fade-up" style={{ marginBottom: 32 }}>
      <SectionTitle icon="user" title="Your Profile" size="lg" />
      <p style={{ fontSize: 14, color: T.textMuted }}>Review your profile before beginning your training journey.</p>
    </div>

    <Card className="fade-up-1" glow style={{ marginBottom: 20, padding: 32 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 28 }}>
        <div style={{ width: 60, height: 60, borderRadius: "50%", background: T.gradient, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 700, color: "#fff", boxShadow: `0 6px 20px ${T.accent}40` }}>AW</div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: T.text }}>Amina Wanjiku</div>
          <div style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>Junior Software Engineer</div>
        </div>
        <div style={{ marginLeft: "auto" }}><Badge color={T.success}>Profile Complete</Badge></div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {[
          { label: "Industry", value: "Tech / Fintech", icon: "grid" },
          { label: "Work Setup", value: "Hybrid — Nairobi", icon: "globe" },
          { label: "Growth Goal", value: "Get promoted to mid-level", icon: "star" },
          { label: "AI Comfort", value: "Basic — tried ChatGPT", icon: "brain" },
        ].map(f => (
          <div key={f.label} style={{ padding: 16, background: T.surfaceAlt, borderRadius: T.radiusSm, border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 10, color: T.textDim, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.8 }}>{f.label}</div>
            <div style={{ fontSize: 14, color: T.text, fontWeight: 500 }}>{f.value}</div>
          </div>
        ))}
      </div>
    </Card>

    <Card className="fade-up-2" style={{ marginBottom: 24, background: `${T.accent}08`, border: `1px solid ${T.accent}25`, padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Icon name="lock" size={20} color={T.accent} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Current Layer: 1 — Workplace Readiness Simulator</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 3 }}>Complete training to unlock Layers 2 & 3</div>
        </div>
      </div>
    </Card>

    <Btn full className="fade-up-3" onClick={() => onNavigate("layer1-hub")} style={{ padding: 16, fontSize: 15 }}>
      Begin Training <Icon name="arrow" size={16} color="#fff" />
    </Btn>
  </PageLayout>
);

// ─── SCREEN 4: Layer 1 Hub ───────────────────────────────────────
const Layer1Hub = ({ onNavigate }) => {
  const pillars = [
    { icon: "chat", title: "Scenario Simulations", desc: "Navigate real workplace situations", progress: 2, total: 3, color: T.accent, screen: "scenario" },
    { icon: "globe", title: "Cultural Decoder", desc: "Interpret cross-cultural communication", progress: 1, total: 2, color: T.purple, screen: "decoder" },
    { icon: "mail", title: "Soft Skills Drills", desc: "Practice emails, meetings, prioritisation", progress: 0, total: 1, color: T.teal, screen: "soft-skills" },
    { icon: "brain", title: "AI Training", desc: "Prompts, judgment, workflows, tools, review", progress: 3, total: 5, color: T.warning, screen: "ai-training" },
  ];
  return (
    <PageLayout maxWidth={900}>
      <div className="fade-up" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: T.text, letterSpacing: -0.5 }}>Layer 1: Readiness Simulator</h1>
            <Badge color={T.warning}>PRE-GATE</Badge>
          </div>
          <p style={{ fontSize: 14, color: T.textMuted }}>Complete all four pillars to unlock your readiness assessment</p>
        </div>
        <Btn variant="secondary" onClick={() => onNavigate("assessment")} style={{ flexShrink: 0 }}>
          <Icon name="lock" size={15} color={T.textMuted} /> Take Assessment
        </Btn>
      </div>

      {/* Overall Progress */}
      <Card className="fade-up-1" style={{ marginBottom: 28, padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Assessment Eligibility</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.warning }}>6 / 11 exercises</span>
        </div>
        <ProgressBar value={6} max={11} color={T.warning} height={8} />
        <div style={{ fontSize: 12, color: T.textMuted, marginTop: 10 }}>Complete 5 more exercises to unlock the readiness assessment</div>
      </Card>

      {/* Pillars Grid */}
      <div className="fade-up-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {pillars.map((p, i) => (
          <Card key={i} className="card-hover" onClick={() => onNavigate(p.screen)} style={{ padding: 24, cursor: "pointer", transition: "all 0.2s" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: `${p.color}15`, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${p.color}20`, flexShrink: 0 }}>
                <Icon name={p.icon} size={22} color={p.color} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{p.title}</span>
                  <span style={{ fontSize: 12, color: p.progress === p.total ? T.success : T.textMuted, fontWeight: 600 }}>{p.progress}/{p.total}</span>
                </div>
                <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>{p.desc}</div>
                <ProgressBar value={p.progress} max={p.total} color={p.progress === p.total ? T.success : p.color} height={4} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Quick links */}
      <div className="fade-up-3" style={{ display: "flex", gap: 10, marginTop: 20 }}>
        <Btn variant="secondary" onClick={() => onNavigate("scenario")} full style={{ fontSize: 13 }}>
          <Icon name="chat" size={14} color={T.textMuted} /> Continue Scenario
        </Btn>
        <Btn variant="secondary" onClick={() => onNavigate("decoder")} full style={{ fontSize: 13 }}>
          <Icon name="globe" size={14} color={T.textMuted} /> Cultural Decoder
        </Btn>
      </div>
    </PageLayout>
  );
};

// ─── SCREEN 5: Scenario ──────────────────────────────────────────
const ScenarioScreen = ({ onNavigate }) => (
  <PageLayout maxWidth={760}>
    <div className="fade-up" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
      <Btn variant="ghost" small onClick={() => onNavigate("layer1-hub")}>← Back</Btn>
      <Badge color={T.accent}>Pillar A</Badge>
      <Badge color={T.warning} bg={T.warningBg}>Level 2</Badge>
    </div>

    <SectionTitle className="fade-up-1" icon="chat" title="Scenario: Status Update Under Pressure" size="lg" />

    <Card className="fade-up-2" style={{ marginBottom: 20, background: `${T.accent}06`, border: `1px solid ${T.accent}18`, padding: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.accent, letterSpacing: 1, marginBottom: 12, textTransform: "uppercase" }}>Situation Brief</div>
      <p style={{ fontSize: 14, color: T.text, lineHeight: 1.7, marginBottom: 12 }}>
        <strong>Setting:</strong> You're a junior developer at a Nairobi fintech startup. Your team lead, Sarah, pings you on Slack at 4:30 PM asking for a status update on the payments API integration. You're behind schedule because the third-party documentation had errors, but you haven't flagged this yet.
      </p>
      <p style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.7, fontStyle: "italic" }}>
        Sarah is in a meeting with the CTO in 30 minutes and needs something to report. How do you respond?
      </p>
    </Card>

    <Card className="fade-up-3" style={{ marginBottom: 20 }}>
      <div style={{ marginBottom: 4 }}>
        <ChatBubble isAI text="Sarah just messaged: 'Hey, quick update on the payments integration? I'm briefing the CTO at 5.' How do you respond?" />
        <ChatBubble text="Hi Sarah! The integration is progressing — I've completed the auth flow and test transactions. I hit a blocker with errors in the third-party docs which cost about a day. I've found a workaround and expect to have it done by Thursday. Happy to hop on a quick call if you need more detail for the CTO." />
        <ChatBubble isAI text="Good proactive communication. Sarah replies: 'Thursday works, but the CTO might push back. Can you quantify the risk if the docs have more errors?' This is escalating — how do you handle it?" />
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <input placeholder="Type your response..." style={{ flex: 1, padding: "13px 18px", borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.surfaceAlt, color: T.text, fontSize: 14, transition: "border-color 0.2s" }} />
        <Btn style={{ padding: "13px 20px" }}>
          <Icon name="send" size={15} color="#fff" /> Send
        </Btn>
      </div>
    </Card>

    <div className="fade-up-4" style={{ display: "flex", gap: 10 }}>
      <Btn variant="secondary" onClick={() => onNavigate("scenario-debrief")} full>
        Skip to Debrief <Icon name="arrow" size={15} color={T.textMuted} />
      </Btn>
    </div>
  </PageLayout>
);

// ─── SCREEN 6: Scenario Debrief ──────────────────────────────────
const ScenarioDebrief = ({ onNavigate }) => (
  <PageLayout maxWidth={760}>
    <div className="fade-up" style={{ marginBottom: 8 }}>
      <Btn variant="ghost" small onClick={() => onNavigate("layer1-hub")}>← Back to Hub</Btn>
    </div>
    <SectionTitle className="fade-up-1" icon="check" title="Scenario Debrief" badge="Completed" size="lg" />

    <Card className="fade-up-2" glow style={{ marginBottom: 20, padding: 28 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 4 }}>Status Update Under Pressure</div>
      <div style={{ display: "flex", gap: 16, fontSize: 13, color: T.textMuted, marginBottom: 24 }}>
        <span>Difficulty reached: <strong style={{ color: T.accent }}>Level 2</strong></span>
        <span>Exchanges: <strong style={{ color: T.text }}>4</strong></span>
      </div>
      <div style={{ display: "flex", gap: 20, justifyContent: "center", marginBottom: 24, flexWrap: "wrap" }}>
        {[
          { label: "Professionalism", score: 4, color: T.success },
          { label: "Clarity", score: 4, color: T.accent },
          { label: "Emotional IQ", score: 3, color: T.purple },
          { label: "Problem-Solving", score: 4, color: T.teal },
        ].map(s => <ScoreCircle key={s.label} score={s.score} max={5} size={68} label={s.label} color={s.color} />)}
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ background: T.gradient, padding: "10px 28px", borderRadius: 24, fontSize: 20, fontWeight: 800, color: "#fff", boxShadow: `0 6px 20px ${T.accent}35` }}>15 / 20</div>
      </div>
    </Card>

    <div className="fade-up-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
      <Card style={{ borderLeft: `3px solid ${T.success}`, padding: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.success, marginBottom: 10 }}>✦ STRENGTHS</div>
        <p style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>You proactively owned the blocker instead of hiding it — showing accountability. You offered a concrete timeline and escalation path.</p>
      </Card>
      <Card style={{ borderLeft: `3px solid ${T.warning}`, padding: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.warning, marginBottom: 10 }}>⚠ GROWTH AREA</div>
        <p style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>When asked to "quantify the risk," your response was vague. Try: <em>"Based on the 3 endpoints remaining, ~40% chance of a 1-day slip."</em></p>
      </Card>
    </div>

    <Card className="fade-up-4" style={{ marginBottom: 20, padding: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: T.accent, marginBottom: 12 }}>READINESS DIMENSIONS PRACTICED</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {["Communication Clarity", "Self-Management", "Collaboration"].map(d => <Badge key={d} color={T.accent}>{d}</Badge>)}
      </div>
    </Card>

    <Card className="fade-up-5" style={{ background: `${T.teal}08`, border: `1px solid ${T.teal}22`, padding: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: T.teal, marginBottom: 8 }}>→ NEXT RECOMMENDED</div>
      <p style={{ fontSize: 13, color: T.text, marginBottom: 14, lineHeight: 1.6 }}>Try a <strong>Cultural Decoder</strong> exercise — your scenario involved cross-cultural timing expectations worth exploring.</p>
      <Btn small onClick={() => onNavigate("decoder")}>Start Cultural Decoder →</Btn>
    </Card>
  </PageLayout>
);

// ─── SCREEN 7: Cultural Decoder ──────────────────────────────────
const DecoderScreen = ({ onNavigate }) => (
  <PageLayout maxWidth={700}>
    <Btn className="fade-up" variant="ghost" small onClick={() => onNavigate("layer1-hub")}>← Back</Btn>
    <div style={{ marginBottom: 28, marginTop: 16 }}>
      <SectionTitle icon="globe" title="Cultural Decoder" badge="Pillar B" size="lg" />
    </div>

    <Card className="fade-up-1" style={{ marginBottom: 24, background: `${T.purple}08`, border: `1px solid ${T.purple}22`, padding: 28 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.purple, letterSpacing: 1, marginBottom: 12, textTransform: "uppercase" }}>Phrase to Decode</div>
      <div style={{ fontSize: 17, fontWeight: 600, color: T.text, lineHeight: 1.6, marginBottom: 16 }}>
        "That's an interesting approach — let's circle back on this after the holidays."
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Badge color={T.purple}>US Corporate → East African</Badge>
        <Badge color={T.textMuted}>Slack message</Badge>
      </div>
    </Card>

    <div className="fade-up-2" style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
      {[
        { pct: 55, title: "Polite rejection", desc: "In US corporate culture, 'interesting approach' + delay tactic often signals disagreement without direct confrontation. The work may not move forward.", color: T.danger },
        { pct: 30, title: "Genuine interest, bad timing", desc: "The holidays are genuinely busy. They liked the idea but need bandwidth. Expect follow-up in January.", color: T.warning },
        { pct: 15, title: "Political deflection", desc: "They need buy-in from someone else before committing. 'Circle back' buys time to consult internally.", color: T.purple },
      ].map((interp, i) => (
        <Card key={i} style={{ borderLeft: `3px solid ${interp.color}`, padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Interpretation {i + 1}: {interp.title}</span>
            <Badge color={interp.color}>~{interp.pct}%</Badge>
          </div>
          <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>{interp.desc}</p>
        </Card>
      ))}
    </div>

    <Card className="fade-up-3" style={{ borderLeft: `3px solid ${T.success}`, padding: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: T.success, marginBottom: 10 }}>✦ SAFE RESPONSE (works across all interpretations)</div>
      <p style={{ fontSize: 14, color: T.text, lineHeight: 1.65, fontStyle: "italic" }}>"Thanks for the feedback! I'll refine the proposal and have an updated version ready for when you're back. Happy holidays!"</p>
    </Card>

    <Btn className="fade-up-4" variant="secondary" onClick={() => onNavigate("layer1-hub")} full style={{ marginTop: 20 }}>
      Practice in Scenario Simulation →
    </Btn>
  </PageLayout>
);

// ─── SCREEN 8: Assessment ─────────────────────────────────────────
const AssessmentScreen = ({ onNavigate }) => {
  const [revealed, setRevealed] = useState(false);
  return (
    <PageLayout maxWidth={700}>
      <SectionTitle className="fade-up" icon={revealed ? "unlock" : "lock"} title="Readiness Assessment" size="lg" />
      {!revealed ? (
        <Card className="fade-up-1" style={{ textAlign: "center", padding: 60 }}>
          <div style={{ width: 90, height: 90, borderRadius: "50%", background: T.surfaceAlt, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 44, border: `2px solid ${T.border}` }}>🔒</div>
          <h3 style={{ fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 12 }}>Ready to test your readiness?</h3>
          <p style={{ fontSize: 14, color: T.textMuted, marginBottom: 32, maxWidth: 400, margin: "0 auto 32px", lineHeight: 1.7 }}>This assessment evaluates 5 dimensions based on everything you've practiced in Layer 1.</p>
          <Btn onClick={() => setRevealed(true)} style={{ padding: "14px 36px", fontSize: 15 }}>Begin Assessment</Btn>
        </Card>
      ) : (
        <>
          <Card className="fade-up-1" style={{ textAlign: "center", marginBottom: 24, padding: 32, background: `${T.success}08`, border: `1px solid ${T.success}25` }}>
            <div style={{ fontSize: 44, marginBottom: 16 }}>🔓</div>
            <h3 style={{ fontSize: 24, fontWeight: 800, color: T.success, marginBottom: 8 }}>READY — Layers 2 & 3 Unlocked!</h3>
            <p style={{ fontSize: 14, color: T.textMuted }}>Average Score: <strong style={{ color: T.text, fontSize: 16 }}>7.2 / 10</strong></p>
          </Card>
          <div className="fade-up-2" style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
            {[
              { dim: "Communication Clarity", score: 8, evidence: "Clear, structured responses in status update scenario" },
              { dim: "Cultural Awareness", score: 7, evidence: "Identified 2/3 interpretations correctly in decoder" },
              { dim: "Self-Management", score: 7, evidence: "Proactive deadline communication, strong prioritisation" },
              { dim: "Collaboration", score: 6, evidence: "Good feedback seeking; room for more stakeholder inclusion" },
              { dim: "AI Fluency", score: 8, evidence: "Strong prompts (avg 20/25), good judgment on ethics scenarios" },
            ].map(d => (
              <Card key={d.dim} style={{ padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{d.dim}</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: d.score >= 7 ? T.success : d.score >= 5 ? T.warning : T.danger }}>{d.score}/10</span>
                </div>
                <ProgressBar value={d.score} max={10} color={d.score >= 7 ? T.success : d.score >= 5 ? T.warning : T.danger} height={5} />
                <p style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>{d.evidence}</p>
              </Card>
            ))}
          </div>
          <Btn className="fade-up-3" full onClick={() => onNavigate("layer2-checkin")} style={{ padding: 16, fontSize: 15 }}>
            Start Weekly Check-in <Icon name="arrow" size={16} color="#fff" />
          </Btn>
        </>
      )}
    </PageLayout>
  );
};

// ─── SCREEN 9: Weekly Check-in ───────────────────────────────────
const CheckinScreen = ({ onNavigate }) => (
  <PageLayout maxWidth={700}>
    <div className="fade-up" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
      <Badge color={T.success}>POST-GATE</Badge>
      <span style={{ fontSize: 13, color: T.textMuted }}>Layer 2 — Week 6</span>
    </div>
    <SectionTitle icon="chat" title="Weekly Check-in" size="lg" />

    <Card className="fade-up-1" style={{ marginBottom: 20, padding: 24 }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
        <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.success, marginTop: 2, flexShrink: 0 }} />
        <div style={{ fontSize: 12, color: T.textMuted, fontWeight: 600, letterSpacing: 0.3 }}>AI COACH — ACTIVE SESSION</div>
      </div>
      <ChatBubble isAI text="Hey Amina! Week 6 — you've been consistent. Last week you mentioned wanting to speak up more in the architecture meetings. How did that go?" />
      <ChatBubble text="Actually, I did speak up on Thursday! I suggested we refactor the auth module. Sarah seemed receptive but the senior dev pushed back a bit." />
      <ChatBubble isAI text="That's a real win — suggesting a refactor in front of seniors takes courage, especially when you're newer to the team. How did you handle the pushback?" />
      <ChatBubble text="I felt a bit deflated honestly. I explained my reasoning but didn't push further. I think I could've backed it up with data from our error logs." />
      <ChatBubble isAI text="Smart reflection — 'backed it up with data' is exactly right. That moves you from sharing an opinion to making a case. One more question: have you used any AI tools this week in your work?" />
      <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
        <input placeholder="Your response..." style={{ flex: 1, padding: "13px 18px", borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.surfaceAlt, color: T.text, fontSize: 14 }} />
        <Btn style={{ padding: "13px 20px" }}><Icon name="send" size={15} color="#fff" /></Btn>
      </div>
    </Card>

    <Btn className="fade-up-2" variant="ghost" small onClick={() => onNavigate("insight-card")}>
      Skip to Insight Card <Icon name="arrow" size={14} color={T.accentLight} />
    </Btn>
  </PageLayout>
);

// ─── SCREEN 10: Insight Card ─────────────────────────────────────
const InsightCard = ({ onNavigate }) => (
  <PageLayout maxWidth={760}>
    <div className="fade-up" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
      <Badge color={T.success}>POST-GATE</Badge>
      <span style={{ fontSize: 13, color: T.textMuted }}>Week 6</span>
    </div>
    <SectionTitle icon="spark" title="Weekly Insight Card" size="lg" />

    <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 16 }}>
      {/* Performance Track */}
      <div className="fade-up-1" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Card glow style={{ padding: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.accent, letterSpacing: 1, marginBottom: 16, textTransform: "uppercase" }}>Performance Track</div>
          <div style={{ padding: 16, background: T.successBg, borderRadius: T.radiusSm, borderLeft: `3px solid ${T.success}`, marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.success, marginBottom: 8 }}>✦ STRENGTH THIS WEEK</div>
            <p style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>You initiated a technical proposal in a senior meeting — shows growing confidence in your technical voice.</p>
          </div>
          <div style={{ padding: 16, background: T.warningBg, borderRadius: T.radiusSm, borderLeft: `3px solid ${T.warning}`, marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.warning, marginBottom: 8 }}>⚠ BLIND SPOT</div>
            <p style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>"I didn't push further" — you had the right instinct but didn't follow through. The gap is between insight and action under social pressure.</p>
          </div>
          <div style={{ padding: 16, background: `${T.accent}10`, borderRadius: T.radiusSm, borderLeft: `3px solid ${T.accent}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.accent, marginBottom: 8 }}>→ ACTION FOR NEXT WEEK</div>
            <p style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>Before your next architecture meeting, prepare one data-backed proposal. Have the evidence ready before you speak.</p>
          </div>
        </Card>
      </div>

      {/* Side cards */}
      <div className="fade-up-2" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.purple, letterSpacing: 1, marginBottom: 14, textTransform: "uppercase" }}>AI Fluency</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <Badge color={T.teal}>Intermediate</Badge>
            <span style={{ fontSize: 20, color: T.success }}>↑</span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: T.purple, marginBottom: 4 }}>6/10</div>
          <p style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>Using Copilot regularly but haven't tried AI for meeting prep yet.</p>
        </Card>
        <Card style={{ borderLeft: `3px solid ${T.pink}`, padding: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.pink, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.8 }}>Emerging Pattern (Wk 4+)</div>
          <p style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>Consistently strong technical instincts but pull back when challenged by senior colleagues.</p>
          <div style={{ marginTop: 10 }}><Badge color={T.pink}>Developing</Badge></div>
        </Card>
      </div>
    </div>

    <div className="fade-up-3" style={{ display: "flex", gap: 10, marginTop: 20 }}>
      <Btn full onClick={() => onNavigate("challenge")}>This Week's Challenge <Icon name="arrow" size={15} color="#fff" /></Btn>
      <Btn variant="secondary" full onClick={() => onNavigate("growth-timeline")}>Growth Timeline</Btn>
    </div>
  </PageLayout>
);

// ─── SCREEN 11: Growth Timeline ──────────────────────────────────
const GrowthTimeline = ({ onNavigate }) => {
  const dims = [
    { name: "Communication", data: [5, 6, 6, 7, 7, 8], color: T.accent },
    { name: "Cultural Awareness", data: [6, 6, 7, 7, 7, 7], color: T.purple },
    { name: "Self-Management", data: [5, 5, 6, 6, 7, 7], color: T.teal },
    { name: "Collaboration", data: [4, 5, 5, 6, 6, 6], color: T.warning },
    { name: "AI Fluency", data: [3, 4, 4, 5, 6, 6], color: T.pink },
    { name: "Overall Confidence", data: [4, 5, 5, 6, 6, 7], color: T.success },
  ];
  const weeks = ["Base", "Wk2", "Wk3", "Wk4", "Wk5", "Wk6"];
  const chartH = 180, chartW = 480;
  return (
    <PageLayout maxWidth={840}>
      <Btn className="fade-up" variant="ghost" small onClick={() => onNavigate("insight-card")}>← Back</Btn>
      <div style={{ marginTop: 16, marginBottom: 24 }}>
        <SectionTitle icon="chart" title="Growth Timeline" badge="Week 6" size="lg" />
      </div>

      <Card className="fade-up-1" glow style={{ marginBottom: 20, padding: 28 }}>
        <div style={{ overflowX: "auto" }}>
          <svg width="100%" height={chartH + 50} viewBox={`0 0 ${chartW + 60} ${chartH + 40}`} style={{ overflow: "visible", minWidth: 400 }}>
            {[0, 2, 4, 6, 8, 10].map(v => (
              <g key={v}>
                <line x1={44} y1={chartH - (v / 10) * chartH} x2={chartW + 44} y2={chartH - (v / 10) * chartH} stroke={T.border} strokeWidth={1} />
                <text x={36} y={chartH - (v / 10) * chartH + 4} textAnchor="end" fill={T.textDim} fontSize={10} fontFamily={T.font}>{v}</text>
              </g>
            ))}
            {weeks.map((w, i) => (
              <text key={w} x={44 + (i / (weeks.length - 1)) * chartW} y={chartH + 22} textAnchor="middle" fill={T.textMuted} fontSize={11} fontFamily={T.font}>{w}</text>
            ))}
            {dims.map(d => (
              <polyline key={d.name} fill="none" stroke={d.color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"
                points={d.data.map((v, i) => `${44 + (i / (d.data.length - 1)) * chartW},${chartH - (v / 10) * chartH}`).join(" ")} />
            ))}
          </svg>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8 }}>
          {dims.map(d => (
            <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: d.color }} />
              <span style={{ fontSize: 12, color: T.textMuted }}>{d.name}</span>
            </div>
          ))}
        </div>
      </Card>

      <div className="fade-up-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Card style={{ padding: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.accent, marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.8 }}>Growth Narrative</div>
          <p style={{ fontSize: 13, color: T.text, lineHeight: 1.7 }}>Communication is your strongest trajectory (+3 since baseline). AI Fluency has doubled but plateaued at 6. Collaboration remains the primary growth edge.</p>
        </Card>
        <Card style={{ padding: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.warning, marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.8 }}>Key Inflection Points</div>
          <div style={{ fontSize: 13, color: T.text, lineHeight: 1.7 }}>
            <p style={{ marginBottom: 10 }}><strong style={{ color: T.accent }}>Week 3:</strong> Communication jumped after completing the cross-cultural scenario simulation.</p>
            <p><strong style={{ color: T.purple }}>Week 5:</strong> First time proactively speaking up in sprint planning.</p>
          </div>
        </Card>
      </div>
    </PageLayout>
  );
};

// ─── SCREEN 12: Weekly Challenge ─────────────────────────────────
const ChallengeScreen = ({ onNavigate }) => (
  <PageLayout maxWidth={760}>
    <div className="fade-up" style={{ display: "flex", gap: 8, marginBottom: 24 }}>
      <Badge color={T.success}>POST-GATE</Badge>
      <Badge color={T.warning} bg={T.warningBg}>Combination Tier</Badge>
    </div>
    <SectionTitle icon="target" title="Weekly Challenge — Week 6" size="lg" />

    <Card className="fade-up-1" glow style={{ marginBottom: 20, padding: 28 }}>
      <div style={{ display: "inline-flex", padding: "5px 14px", background: T.gradient, borderRadius: 6, fontSize: 11, fontWeight: 700, color: "#fff", marginBottom: 16 }}>COMBINATION CHALLENGE</div>
      <h3 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 12 }}>The Data-Backed Proposal</h3>
      <p style={{ fontSize: 14, color: T.text, lineHeight: 1.7, marginBottom: 20 }}>Prepare a 1-page technical proposal for the auth module refactor you suggested this week. Include error log data, estimated effort, and risk assessment. Present it to one colleague for feedback before sharing wider.</p>

      <Card style={{ background: `${T.accent}08`, border: `1px solid ${T.accent}18`, padding: 16, marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.accent, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.8 }}>Why This Challenge</div>
        <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>Your insight card identified an "insight-to-action gap." This challenge pairs <strong style={{ color: T.text }}>Communication</strong> (your strength) with <strong style={{ color: T.text }}>Collaboration</strong> (your growth edge).</p>
      </Card>

      <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 10 }}>SKILLS PRACTICED</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Badge color={T.accent}>Communication</Badge>
            <Badge color={T.warning}>Collaboration</Badge>
            <Badge color={T.purple}>AI-Assisted</Badge>
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 10 }}>COMPLETION CRITERIA</div>
          {["Draft 1-page proposal with data", "Use AI to help structure it", "Get feedback from 1 colleague", "Revise based on feedback"].map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0" }}>
              <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${T.border}`, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: T.text }}>{c}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, padding: "14px 0", borderTop: `1px solid ${T.border}`, fontSize: 13, color: T.textMuted }}>
        <span>⏱ Est. time: <strong style={{ color: T.text }}>45 min</strong></span>
        <span>🤖 AI Angle: <strong style={{ color: T.purple }}>USE AI</strong></span>
      </div>
    </Card>

    <Card className="fade-up-2" style={{ background: `${T.teal}08`, border: `1px solid ${T.teal}22`, marginBottom: 20, padding: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.teal, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.8 }}>Stretch Option</div>
      <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>Present the proposal in your next architecture meeting and document the team's response. This elevates to a <strong style={{ color: T.text }}>Compound</strong> challenge.</p>
    </Card>

    <div className="fade-up-3" style={{ display: "flex", gap: 10 }}>
      <Btn full onClick={() => onNavigate("challenge-feedback")}>Submit Challenge Output</Btn>
      <Btn variant="secondary" full onClick={() => onNavigate("ghost-mentor")}>Ask Ghost Mentor</Btn>
    </div>
  </PageLayout>
);

// ─── SCREEN 13: Challenge Feedback ───────────────────────────────
const ChallengeFeedback = ({ onNavigate }) => (
  <PageLayout maxWidth={720}>
    <Btn className="fade-up" variant="ghost" small onClick={() => onNavigate("challenge")}>← Back</Btn>
    <div style={{ marginTop: 16, marginBottom: 24 }}>
      <SectionTitle icon="check" title="Challenge Feedback" badge="Week 6" size="lg" />
    </div>

    <Card className="fade-up-1" glow style={{ marginBottom: 20, padding: 28 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.success, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.8 }}>What You Did Well</div>
          <p style={{ fontSize: 13, color: T.text, lineHeight: 1.7 }}>Your proposal was well-structured — opening with the error count (47 errors in 2 weeks) immediately made the case concrete. Using AI to generate the risk matrix was smart, and you correctly adjusted the AI output where it underestimated migration effort.</p>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.warning, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.8 }}>What Could Improve</div>
          <p style={{ fontSize: 13, color: T.text, lineHeight: 1.7 }}>The "estimated effort" section relied on gut feel rather than data. Try: reference similar past refactors, or use AI to benchmark typical auth module migrations.</p>
        </div>
      </div>

      <Divider label="SKILL ASSESSMENT" />
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {[
          { skill: "Communication Clarity", level: 4, evidence: "Clear structure, data-led opening" },
          { skill: "Collaboration", level: 3, evidence: "Got feedback but from a peer, not a senior" },
          { skill: "AI Integration", level: 4, evidence: "Good AI use; correctly edited AI output" },
        ].map(s => (
          <div key={s.skill} style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 160, fontSize: 13, color: T.textMuted, flexShrink: 0 }}>{s.skill}</div>
            <div style={{ display: "flex", gap: 4 }}>
              {[1,2,3,4,5].map(i => <div key={i} style={{ width: 20, height: 20, borderRadius: 4, background: i <= s.level ? T.accent : T.surfaceAlt, border: `1px solid ${i <= s.level ? T.accent : T.border}` }} />)}
            </div>
            <span style={{ fontSize: 12, color: T.textDim }}>{s.evidence}</span>
          </div>
        ))}
      </div>
    </Card>

    <Card className="fade-up-2" style={{ borderLeft: `3px solid ${T.teal}`, marginBottom: 20, padding: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: T.teal, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.8 }}>Journal Prompt for Next Check-in</div>
      <p style={{ fontSize: 14, color: T.text, lineHeight: 1.65, fontStyle: "italic" }}>"When you presented the proposal and got feedback, what surprised you? What would you do differently if you could rewind?"</p>
    </Card>

    <Btn className="fade-up-3" variant="secondary" full onClick={() => onNavigate("dashboard")}>
      View Full Dashboard →
    </Btn>
  </PageLayout>
);

// ─── SCREEN 14: Ghost Mentor ─────────────────────────────────────
const GhostMentorScreen = ({ onNavigate }) => (
  <PageLayout maxWidth={760}>
    <Btn className="fade-up" variant="ghost" small onClick={() => onNavigate("challenge")}>← Back</Btn>
    <div style={{ marginTop: 16, marginBottom: 24 }}>
      <SectionTitle icon="mentor" title="Ghost Mentor Session" size="lg" />
    </div>

    <Card className="fade-up-1" glow style={{ marginBottom: 20, borderTop: `3px solid ${T.purple}`, padding: 28 }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <Badge color={T.purple}>Proxy Match</Badge>
        <Badge color={T.textMuted}>High Relevance</Badge>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <div style={{ padding: 16, background: T.surfaceAlt, borderRadius: T.radiusSm, border: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.accent, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.8 }}>Your Situation</div>
          <p style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>A junior developer with strong technical instincts who struggles to assert ideas when challenged by senior colleagues. You've identified the problem but need strategies to bridge insight-to-action.</p>
        </div>
        <div style={{ padding: 16, background: `${T.purple}08`, borderRadius: T.radiusSm, border: `1px solid ${T.purple}20` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.purple, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.8 }}>Matched Success Case</div>
          <p style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>A junior data engineer at a Nairobi fintech who went from "invisible in meetings" to leading a data pipeline refactor within 6 months.</p>
          <p style={{ fontSize: 11, color: T.textMuted, marginTop: 8 }}>Match type: <strong style={{ color: T.text }}>Proxy</strong> — similar role dynamics</p>
        </div>
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}>How They Succeeded</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {[
          { step: "1", title: "Pre-meeting Slack drops", desc: "Shared data findings in the team channel 24hrs before meetings, so ideas had 'soak time.'", you: "You could share your error log analysis in #engineering before the next architecture meeting." },
          { step: "2", title: "Ally recruitment", desc: "Found one senior who was sympathetic and got their buy-in privately first.", you: "Sarah seemed receptive to your refactor idea — she could be your ally." },
          { step: "3", title: "Evidence-first framing", desc: "Always led with numbers instead of opinions.", you: "Your 47-error data point is exactly this approach — lean into it." },
        ].map(s => (
          <div key={s.step} style={{ padding: 16, background: T.surfaceAlt, borderRadius: T.radiusSm, border: `1px solid ${T.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: T.gradient, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>{s.step}</div>
              <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{s.title}</span>
            </div>
            <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 8, lineHeight: 1.6 }}>{s.desc}</p>
            <p style={{ fontSize: 13, color: T.accent, lineHeight: 1.6 }}>→ Your data: {s.you}</p>
          </div>
        ))}
      </div>
    </Card>

    <Btn className="fade-up-2" variant="secondary" full onClick={() => onNavigate("career-paths")}>Explore Career Paths →</Btn>
  </PageLayout>
);

// ─── SCREEN 15: Career Paths ─────────────────────────────────────
const CareerPaths = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState("Path A");
  const paths = {
    "Path A": { risk: "Low Risk", color: T.success, title: "Senior Developer Track", timeline: "12–18 months", desc: "Natural progression building on your technical strengths and growing communication skills. Stay at your current company, deepen backend expertise, and position for promotion.", why: "Your Communication score jumped from 5→8, and your technical instincts are consistently praised. You're building the exact skills your company promotes for.", skills: ["System design", "Technical writing", "Sprint leadership"], market: { demand: "High", salary: "KES 180–280K/mo", ai: "Medium disruption risk" }, steps: ["Complete 2 more compound challenges focused on technical leadership", "Volunteer to own one system design document this quarter", "Request a 1:1 with your engineering manager about promotion criteria"] },
    "Path B": { risk: "Medium Risk", color: T.warning, title: "Developer Relations / DevRel", timeline: "6–12 months to first role", desc: "Strategic pivot leveraging your communication strength in a technical context. Your scenario simulations show strong audience adaptation — that's a DevRel superpower.", why: "Communication is your fastest-growing dimension, and you naturally explain technical concepts clearly. Your cross-cultural awareness (7/10) is above average for the field.", skills: ["Public speaking", "Content creation", "Community management"], market: { demand: "Growing", salary: "KES 200–350K/mo", ai: "Low disruption risk" }, steps: ["Write one technical blog post this month about your auth refactor experience", "Join the Nairobi DevRel community meetup", "Build a small demo project you can present at a local tech event"] },
    "Path C": { risk: "High Risk / High Reward", color: T.pink, title: "AI Product Manager", timeline: "18–24 months", desc: "The path you haven't considered. Your AI fluency trajectory (3→6 in 6 weeks), combined with your emerging ability to bridge technical and business perspectives, maps to a rapidly growing role.", why: "Most people in your position see 'AI' as a tool skill. But your pattern of translating technical insights into business cases, plus your AI judgment scores, suggests product thinking.", skills: ["Product strategy", "AI/ML fundamentals", "Stakeholder management"], market: { demand: "Very high", salary: "KES 250–450K/mo", ai: "Creator, not disrupted" }, steps: ["Take an online AI product management course", "Start tracking AI-related decisions at your company — who makes them, how", "Join a product community and attend 2 events this quarter"] },
  };
  const p = paths[activeTab];
  return (
    <PageLayout maxWidth={800}>
      <Btn className="fade-up" variant="ghost" small onClick={() => onNavigate("ghost-mentor")}>← Back</Btn>
      <div style={{ marginTop: 16, marginBottom: 24 }}>
        <SectionTitle icon="path" title="Career Path Recommendations" badge="Week 6" size="lg" />
      </div>

      <TabBar tabs={["Path A", "Path B", "Path C"]} active={activeTab} onChange={setActiveTab} />

      <Card className="fade-up-1" glow style={{ borderTop: `3px solid ${p.color}`, padding: 32 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <h3 style={{ fontSize: 22, fontWeight: 800, color: T.text, marginBottom: 8 }}>{p.title}</h3>
            <div style={{ fontSize: 13, color: T.textMuted }}>Timeline: <strong style={{ color: T.text }}>{p.timeline}</strong></div>
          </div>
          <Badge color={p.color}>{p.risk}</Badge>
        </div>
        <p style={{ fontSize: 14, color: T.text, lineHeight: 1.7, marginBottom: 20 }}>{p.desc}</p>

        <div style={{ padding: 16, background: `${p.color}10`, borderRadius: T.radiusSm, marginBottom: 20, border: `1px solid ${p.color}20` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: p.color, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.8 }}>Why It Fits You</div>
          <p style={{ fontSize: 13, color: T.text, lineHeight: 1.65 }}>{p.why}</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
          <div style={{ padding: 16, background: T.surfaceAlt, borderRadius: T.radiusSm, border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.8 }}>Skills to Build</div>
            {p.skills.map(s => <div key={s} style={{ fontSize: 13, color: T.text, padding: "4px 0", borderBottom: `1px solid ${T.border}` }}>• {s}</div>)}
          </div>
          <div style={{ padding: 16, background: T.surfaceAlt, borderRadius: T.radiusSm, border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.8 }}>Market Context</div>
            <div style={{ fontSize: 13, color: T.text, padding: "4px 0", borderBottom: `1px solid ${T.border}` }}>Demand: <strong>{p.market.demand}</strong></div>
            <div style={{ fontSize: 13, color: T.text, padding: "4px 0", borderBottom: `1px solid ${T.border}` }}>Salary: <strong>{p.market.salary}</strong></div>
            <div style={{ fontSize: 13, color: T.text, padding: "4px 0" }}>{p.market.ai}</div>
          </div>
        </div>

        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>Next 3 Steps</div>
        {p.steps.map((s, i) => (
          <div key={i} style={{ display: "flex", gap: 12, marginBottom: 10 }}>
            <div style={{ width: 24, height: 24, borderRadius: "50%", background: p.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>{i + 1}</div>
            <span style={{ fontSize: 13, color: T.text, lineHeight: 1.6, paddingTop: 2 }}>{s}</span>
          </div>
        ))}
      </Card>

      <Btn className="fade-up-2" full onClick={() => onNavigate("dashboard")} style={{ marginTop: 20, padding: 16, fontSize: 15 }}>
        View Full Dashboard <Icon name="arrow" size={16} color="#fff" />
      </Btn>
    </PageLayout>
  );
};

// ─── SCREEN 16: Dashboard ─────────────────────────────────────────
const Dashboard = ({ onNavigate }) => (
  <PageLayout maxWidth={1100}>
    <div className="fade-up" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: T.text, letterSpacing: -0.5 }}>Career Growth Dashboard</h1>
        </div>
        <p style={{ fontSize: 14, color: T.textMuted }}>Amina Wanjiku · Junior Software Engineer · Week 6</p>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Badge color={T.success}>Simulator ✓</Badge>
        <Badge color={T.success}>Engine ✓</Badge>
        <Badge color={T.success}>Accelerate ✓</Badge>
      </div>
    </div>

    {/* Top stats */}
    <div className="fade-up-1" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14, marginBottom: 20 }}>
      {[
        { v: "6", l: "Check-ins", color: T.accent },
        { v: "6🔥", l: "Week Streak", color: T.success },
        { v: "2", l: "Patterns", color: T.purple },
        { v: "5", l: "Challenges", color: T.teal },
        { v: "7.2", l: "Avg Score", color: T.warning },
      ].map(s => <StatBox key={s.l} value={s.v} label={s.l} color={s.color} />)}
    </div>

    {/* Main grid */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      {/* Readiness */}
      <Card className="fade-up-2" style={{ padding: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.accent, marginBottom: 16, textTransform: "uppercase", letterSpacing: 0.8 }}>Readiness Foundation (Layer 1)</div>
        <div style={{ display: "flex", justifyContent: "space-around" }}>
          {[{ l: "Comms", s: 8 }, { l: "Cultural", s: 7 }, { l: "Self-Mgmt", s: 7 }, { l: "Collab", s: 6 }, { l: "AI", s: 8 }].map(d => (
            <ScoreCircle key={d.l} score={d.s} size={52} label={d.l} color={d.s >= 7 ? T.success : T.warning} />
          ))}
        </div>
      </Card>

      {/* Performance Trajectory */}
      <Card className="fade-up-2" style={{ padding: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.purple, marginBottom: 16, textTransform: "uppercase", letterSpacing: 0.8 }}>Performance Trajectory (Layer 2)</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[
            { l: "Communication Clarity", v: 8, c: T.accent },
            { l: "Strength: Technical Communication", badge: true, color: T.success },
            { l: "Growth: Asserting Under Pressure", badge: true, color: T.warning },
          ].map((r, i) => r.badge ? (
            <div key={i}><Badge color={r.color}>{r.l}</Badge></div>
          ) : (
            <ProgressBar key={i} value={r.v} max={10} color={r.c} label={r.l} height={5} />
          ))}
        </div>
      </Card>

      {/* AI Fluency */}
      <Card className="fade-up-3" style={{ padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: T.pink, textTransform: "uppercase", letterSpacing: 0.8 }}>AI Fluency Progression</span>
          <Badge color={T.pink}>6/10</Badge>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {["M1: 3", "M2: 5", "M3: 6"].map((m, i) => (
            <span key={i} style={{ padding: "4px 10px", background: T.surfaceAlt, borderRadius: 6, fontSize: 12, color: T.text, border: `1px solid ${T.border}` }}>{m}</span>
          ))}
          <span style={{ padding: "4px 10px", color: T.success, fontSize: 12 }}>↑ Growing</span>
        </div>
        <ProgressBar value={6} max={10} color={T.pink} height={6} label="Current Level" />
      </Card>

      {/* Career Direction */}
      <Card className="fade-up-3" style={{ padding: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.warning, marginBottom: 16, textTransform: "uppercase", letterSpacing: 0.8 }}>Career Direction</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <Badge color={T.success}>Exploring: Senior Dev</Badge>
          <Badge color={T.warning}>Considering: DevRel</Badge>
          <Badge color={T.pink}>Surprise: AI PM</Badge>
        </div>
        <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>No direction adopted yet — exploring options. <button onClick={() => onNavigate("career-paths")} style={{ color: T.accent, background: "none", border: "none", cursor: "pointer", fontSize: 13, textDecoration: "underline", fontFamily: T.font }}>View path analysis →</button></p>
      </Card>

      {/* Challenges */}
      <Card className="fade-up-4" style={{ padding: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.teal, marginBottom: 16, textTransform: "uppercase", letterSpacing: 0.8 }}>Challenges Completed</div>
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          {[{ l: "Total", v: "5" }, { l: "Streak", v: "4🔥" }, { l: "Foundation", v: "3" }, { l: "Combination", v: "2" }].map(c => (
            <StatBox key={c.l} value={c.v} label={c.l} color={T.teal} />
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, flexWrap: "wrap" }}>
          <Badge color={T.accent} bg={`${T.accent}15`}>Comms</Badge>
          <span style={{ color: T.textDim }}>──</span>
          <Badge color={T.accent} bg={`${T.accent}15`}>Comms+Collab</Badge>
          <span style={{ color: T.textDim }}>──</span>
          <Badge color={T.purple} bg={`${T.purple}15`}>+AI</Badge>
        </div>
      </Card>

      {/* Next Milestone */}
      <Card className="fade-up-4" style={{ background: `${T.accent}08`, border: `1px solid ${T.accent}25`, padding: 24 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: `${T.accent}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon name="star" size={20} color={T.accent} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.accent, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.8 }}>Next Milestone</div>
            <p style={{ fontSize: 14, color: T.text, lineHeight: 1.7 }}>Complete your first Compound challenge — you're <strong>1 week away</strong> from Compound tier eligibility!</p>
            <Btn small style={{ marginTop: 14 }} onClick={() => onNavigate("challenge")}>Take the Challenge</Btn>
          </div>
        </div>
      </Card>
    </div>
  </PageLayout>
);

// ─── SCREEN: Soft Skills ──────────────────────────────────────────
const SoftSkillsScreen = ({ onNavigate }) => (
  <PageLayout maxWidth={720}>
    <Btn className="fade-up" variant="ghost" small onClick={() => onNavigate("layer1-hub")}>← Back</Btn>
    <div style={{ marginTop: 16, marginBottom: 24 }}>
      <SectionTitle icon="mail" title="Soft Skills Drill: Email Writing" badge="Pillar C" size="lg" />
    </div>
    <Card className="fade-up-1" style={{ marginBottom: 20, background: `${T.teal}08`, border: `1px solid ${T.teal}20`, padding: 24 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: T.teal, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.8 }}>Context</div>
      <p style={{ fontSize: 14, color: T.text, lineHeight: 1.7 }}>Your project manager just moved the demo deadline up by 3 days. You need to email your team lead requesting a scope adjustment. The client is watching, and the PM cc'd the whole team.</p>
    </Card>
    <Card className="fade-up-2" style={{ marginBottom: 20, padding: 24 }}>
      <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 16, fontWeight: 500 }}>Write your email below:</div>
      <div style={{ background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: 16 }}>
        <div style={{ fontSize: 13, color: T.textDim, marginBottom: 6, fontFamily: T.fontMono }}>To: sarah@company.co</div>
        <div style={{ fontSize: 13, color: T.textDim, marginBottom: 16, fontFamily: T.fontMono }}>Subject: Demo scope adjustment proposal</div>
        <textarea placeholder="Start typing your email draft..." style={{ width: "100%", minHeight: 160, background: "transparent", border: "none", color: T.text, fontSize: 14, lineHeight: 1.7, resize: "vertical", fontFamily: T.font }} />
      </div>
    </Card>
    <Btn className="fade-up-3" full onClick={() => onNavigate("layer1-hub")} style={{ padding: 15, fontSize: 14 }}>
      Submit Draft for Review
    </Btn>
  </PageLayout>
);

// ─── SCREEN: AI Training ──────────────────────────────────────────
const AITrainingScreen = ({ onNavigate }) => (
  <PageLayout maxWidth={720}>
    <Btn className="fade-up" variant="ghost" small onClick={() => onNavigate("layer1-hub")}>← Back</Btn>
    <div style={{ marginTop: 16, marginBottom: 24 }}>
      <SectionTitle icon="brain" title="AI Training" badge="Pillar D — Mandatory" size="lg" />
    </div>
    <div className="fade-up-1" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {[
        { title: "Prompt Engineering", desc: "Write effective prompts for work tasks", done: true, score: "21/25" },
        { title: "AI Judgment Scenarios", desc: "Navigate ethical AI dilemmas", done: true, score: "4/5" },
        { title: "Role-Specific Workflows", desc: "Plan AI-assisted work processes", done: true, score: "19/25" },
        { title: "Tool Landscape", desc: "Match AI tools to work tasks", done: false },
        { title: "AI Output Review", desc: "Identify errors in AI-generated content", done: false },
      ].map((m, i) => (
        <Card key={i} className="card-hover" onClick={() => {}} style={{ padding: 20, cursor: "pointer", borderLeft: m.done ? `3px solid ${T.success}` : `3px solid ${T.border}`, transition: "all 0.2s" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Module {i + 1}: {m.title}</div>
              <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>{m.desc}</div>
            </div>
            {m.done ? <Badge color={T.success}>{m.score}</Badge> : <Badge color={T.textDim}>Not started</Badge>}
          </div>
        </Card>
      ))}
    </div>
    <Btn className="fade-up-2" variant="secondary" full onClick={() => onNavigate("layer1-hub")} style={{ marginTop: 20 }}>
      Back to Training Hub
    </Btn>
  </PageLayout>
);

// ─── MAIN APP ────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("welcome");
  const scrollRef = useRef(null);

  const navigate = (s) => {
    setScreen(s);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const screens = {
    welcome: WelcomeScreen,
    onboarding: OnboardingScreen,
    profile: ProfileScreen,
    "layer1-hub": Layer1Hub,
    scenario: ScenarioScreen,
    "scenario-debrief": ScenarioDebrief,
    decoder: DecoderScreen,
    "soft-skills": SoftSkillsScreen,
    "ai-training": AITrainingScreen,
    assessment: AssessmentScreen,
    "layer2-checkin": CheckinScreen,
    "insight-card": InsightCard,
    "growth-timeline": GrowthTimeline,
    challenge: ChallengeScreen,
    "challenge-feedback": ChallengeFeedback,
    "ghost-mentor": GhostMentorScreen,
    "career-paths": CareerPaths,
    dashboard: Dashboard,
  };

  const Screen = screens[screen] || WelcomeScreen;

  return (
    <>
      <FontLoader />
      <TopNav screen={screen} onNavigate={navigate} />
      <Screen key={screen} onNavigate={navigate} />
    </>
  );
}